import { useState, useCallback } from 'react'
import { PageShell } from '@components/layout/PageShell.js'
import { ModePageHeader } from '@components/layout/ModePageHeader.js'
import { Button } from '@components/primitives/Button.js'
import { EmptyPanel } from '@components/primitives/EmptyPanel.js'
import { NavIcon } from '@components/layout/NavIcons.js'
import { MateriaisPageSkeleton } from '@components/primitives/Skeleton.js'
import { useOrcamentosMateriais } from '@hooks/useOrcamentosMateriais.js'
import { OrcamentoFormModal } from '@components/materiais/OrcamentoFormModal.js'
import { OrcamentoLoteModal } from '@components/materiais/OrcamentoLoteModal.js'
import { EscolherOrcamentoModal } from '@components/materiais/EscolherOrcamentoModal.js'
import { ConfirmDeleteCotacaoModal } from '@components/materiais/ConfirmDeleteCotacaoModal.js'
import { ConfirmPagarMaterialModal } from '@components/materiais/ConfirmPagarMaterialModal.js'
import { CotacaoRow } from '@components/materiais/CotacaoRow.js'
import type { OrcamentoMaterial } from '@services/db.js'

export function MateriaisPage() {
  const { grupos, isLoading, add, addBulk, remove, escolher, update, marcarPago } =
    useOrcamentosMateriais()
  const [formOpen, setFormOpen] = useState(false)
  const [loteOpen, setLoteOpen] = useState(false)
  const [formGrupoId, setFormGrupoId] = useState<string | undefined>()
  const [formMaterialNome, setFormMaterialNome] = useState<string | undefined>()
  const [escolherGrupo, setEscolherGrupo] = useState<OrcamentoMaterial[] | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<OrcamentoMaterial | null>(null)
  const [pagarTarget, setPagarTarget] = useState<OrcamentoMaterial | null>(null)

  const grupoIds = Object.keys(grupos).sort((a, b) => {
    const ma = grupos[a]![0]?.material ?? ''
    const mb = grupos[b]![0]?.material ?? ''
    return ma.localeCompare(mb, 'pt-BR')
  })

  const openNew = useCallback((grupoId?: string, materialNome?: string) => {
    setFormGrupoId(grupoId)
    setFormMaterialNome(materialNome)
    setFormOpen(true)
  }, [])

  if (isLoading) {
    return (
      <PageShell>
        <MateriaisPageSkeleton label='Carregando orçamentos…' />
      </PageShell>
    )
  }

  return (
    <PageShell>
      <ModePageHeader
        title='Cotações de materiais'
        subtitle='Compare fornecedores. A escolha vira previsto no Financeiro; pagar ou anexar NF marca como pago.'
      />

      <div className='cf3-mat-toolbar'>
        <Button
          onClick={() => setLoteOpen(true)}
          aria-label='Orçamento do fornecedor com vários itens'
        >
          + Orçamento do fornecedor
        </Button>
        <Button variant='secondary' onClick={() => openNew()} aria-label='Cotação de um material'>
          + Um material
        </Button>
      </div>

      {grupoIds.length === 0 ? (
        <EmptyPanel
          icon={<NavIcon icon='obra' size={24} />}
          title='Nenhuma cotação ainda'
          description='Compare fornecedores por material: cadastre um orçamento completo ou cotações avulsas e escolha o melhor preço.'
          actionLabel='Orçamento do fornecedor'
          onAction={() => setLoteOpen(true)}
          secondaryActionLabel='Um material'
          onSecondaryAction={() => openNew()}
        />
      ) : (
        <div className='cf3-mat-grupos'>
          {grupoIds.map((grupoId) => {
            const items = grupos[grupoId]!
            const material = items[0]?.material ?? 'Material'
            const escolhido = items.find((o) => o.status === 'escolhido' || o.status === 'comprado')
            const sorted = [...items].sort((a, b) => a.valorTotal - b.valorTotal)
            return (
              <section key={grupoId} className='cf3-mat-grupo'>
                <div className='cf3-mat-grupo__head'>
                  <div className='cf3-mat-grupo__info'>
                    <h2 className='cf3-mat-grupo__title'>{material}</h2>
                    <p className='cf3-mat-grupo__sub'>
                      {items.length} cotação{items.length > 1 ? 'ões' : ''}
                      {escolhido ? ` · ${escolhido.fornecedor}` : ''}
                    </p>
                  </div>
                  <div className='cf3-mat-grupo__actions'>
                    <Button
                      variant='secondary'
                      size='sm'
                      onClick={() => openNew(grupoId, material)}
                    >
                      + Cotação
                    </Button>
                    {!escolhido && (
                      <Button size='sm' onClick={() => setEscolherGrupo(items)}>
                        Escolher
                      </Button>
                    )}
                  </div>
                </div>
                <div className='cf3-mat-list'>
                  {sorted.map((o) => (
                    <CotacaoRow
                      key={o.id}
                      item={o}
                      {...(o.status === 'escolhido' || o.status === 'comprado'
                        ? { onNf: () => setEscolherGrupo([o]) }
                        : {})}
                      {...(o.status === 'escolhido' ? { onPagar: () => setPagarTarget(o) } : {})}
                      onDelete={() => setDeleteTarget(o)}
                    />
                  ))}
                </div>
              </section>
            )
          })}
        </div>
      )}

      <OrcamentoFormModal
        open={formOpen}
        {...(formGrupoId !== undefined ? { grupoId: formGrupoId } : {})}
        {...(formMaterialNome !== undefined ? { materialNome: formMaterialNome } : {})}
        onClose={() => {
          setFormOpen(false)
          setFormGrupoId(undefined)
          setFormMaterialNome(undefined)
        }}
        onSave={add}
      />

      <OrcamentoLoteModal open={loteOpen} onClose={() => setLoteOpen(false)} onSave={addBulk} />

      {escolherGrupo && (
        <EscolherOrcamentoModal
          open
          items={escolherGrupo}
          onClose={() => setEscolherGrupo(null)}
          onEscolher={escolher}
          onUpdate={update}
        />
      )}

      {pagarTarget && (
        <ConfirmPagarMaterialModal
          open
          item={pagarTarget}
          onClose={() => setPagarTarget(null)}
          onConfirm={async () => {
            if (pagarTarget.id) await marcarPago(pagarTarget.id)
          }}
        />
      )}

      {deleteTarget && (
        <ConfirmDeleteCotacaoModal
          open
          nome={`${deleteTarget.fornecedor} · ${deleteTarget.material}`}
          onClose={() => setDeleteTarget(null)}
          onConfirm={async () => {
            if (deleteTarget.id) await remove(deleteTarget.id)
            setDeleteTarget(null)
          }}
        />
      )}
    </PageShell>
  )
}
